// @flow
import { css, ThemeProvider } from "styled-components";
import { Switch, Redirect, Route } from "react-router-dom";
import { PersistGate } from "redux-persist/lib/integration/react";
import { Provider } from "react-redux";
import {
  GlobalStyles,
  HorizontalLine,
  MetaTags,
  theme,
  ResponsiveImage,
  smallParagraph,
} from "@nested/brand";
import { AVMValuationResult } from "@nested/marketing-site/app/pages/AVMValuationResult/AVMValuationResult";
import { PersonalisedAVMValuationResult } from "@nested/marketing-site/app/pages/PersonalisedAVMValuationResult/PersonalisedAVMValuationResult";
import { Navigation } from "./components/Navigation";
import { PersonalInformationForm } from "./forms/PersonalInformationForm";
import { OnlineValuationForm } from "./forms/OnlineValuationForm";
import { PersonalValuationForm } from "./forms/PersonalValuationForm";
import { FinishingUpForm } from "./forms/FinishingUpForm";
import { ComingSoon, ComingSoonThankYou } from "./forms/ComingSoon";
import { ThankYou } from "./ThankYou";
import { NotInServicedAreaForm } from "./forms/NotInServicedAreaForm";
import { ExploreMarketTrendsForm } from "./forms/ExploreMarketTrendsForm";
import { ErrorHandler } from "./components/ErrorHandler";
import { store, persistor } from "./store";
import { ManualAddressForm } from "./forms/ManualAddressForm";
import { ManualOnboardingThankYou } from "./ManualOnboardingFormThankYou";

const backgroundStyle = css`
  width: 100%;
  min-height: 100vh;
  background-color: ${theme.palette.sand40};
  position: relative;
`;

const wrapperStyle = css`
  width: 100%;
  margin: 0px auto;
  max-width: 1440px;
  background-color: white;
  min-height: 100vh;
`;

const loadingWrapper = css`
  min-height: 80vh;
  display: flex;
`;

const imageWrapper = css`
  width: 234px;
  height: 234px;
  margin: auto;
  border-radius: 100%;
  background-color: ${theme.palette.sand40};
  text-align: center;
`;

const loadingTextStyles = css`
  text-transform: uppercase;
  letter-spacing: 3px;
  color: ${theme.palette.hague};
  font-weight: 500;
  ${smallParagraph}
`;

const imageStyles = css`
  width: 98px;
  height: 96px;
  padding: 44px 0 10px 78px;
  display: block;
`;

export const Loading = () => (
  <div css={loadingWrapper}>
    <div css={imageWrapper}>
      <ResponsiveImage
        alt="loading"
        src="https://images.prismic.io/nested/23d79ea3-ca24-4556-b17d-34f8c6703cf2_wise_owl.png?auto=compress,format"
        css={imageStyles}
      />
      <span css={loadingTextStyles}>
        Consulting
        <br />
        data owl
      </span>
    </div>
  </div>
);

const Providers = ({ children }: { children: React$Node }) => (
  <Provider store={store}>
    <ThemeProvider theme={theme}>{children}</ThemeProvider>
  </Provider>
);

export const OnboardingFormWithoutProviders = () => {
  return (
    <>
      <MetaTags>
        <meta name="robots" content="noindex" />
        <script
          type="text/javascript"
          src="https://assets.calendly.com/assets/external/widget.js"
        />
      </MetaTags>
      <GlobalStyles />
      <div css={backgroundStyle}>
        <div css={wrapperStyle}>
          <Navigation />
          <HorizontalLine />
          <ErrorHandler>
            <PersistGate loading={<Loading />} persistor={persistor}>
              <Switch>
                <Redirect from="/get-started/coronavirus" to="/get-started" />
                <Route
                  path="/get-started/onboarding/thank-you"
                  component={ManualOnboardingThankYou}
                  exact
                />
                <Route
                  path="/get-started/onboarding"
                  component={ManualAddressForm}
                />
                <Route
                  path="/get-started/personalised-valuation-result/:id"
                  component={PersonalisedAVMValuationResult}
                />
                <Route
                  path="/get-started/valuation-result/:id"
                  component={AVMValuationResult}
                />
                <Route
                  path="/get-started/postcode-not-in-serviced-area"
                  component={NotInServicedAreaForm}
                />
                <Route
                  path="/get-started/explore-market-trends"
                  component={ExploreMarketTrendsForm}
                />
                <Route
                  path="/get-started/online-valuation"
                  component={OnlineValuationForm}
                />
                <Route
                  path="/get-started/personal-valuation"
                  component={PersonalValuationForm}
                />
                <Route
                  path="/get-started/coming-soon"
                  component={ComingSoon}
                  exact
                />
                <Route
                  path="/get-started/coming-soon/thank-you"
                  component={ComingSoonThankYou}
                  exact
                />
                <Route path="/get-started/thank-you" component={ThankYou} />
                <Route
                  path="/get-started/finishing-up"
                  component={FinishingUpForm}
                />
                <Route
                  path="/get-started/callback"
                  component={FinishingUpForm}
                />
                <Route
                  path="/get-started"
                  component={PersonalInformationForm}
                />
              </Switch>
            </PersistGate>
          </ErrorHandler>
        </div>
      </div>
    </>
  );
};

export const OnboardingForm = () => (
  <Providers>
    <OnboardingFormWithoutProviders />
  </Providers>
);
